import Constants from 'expo-constants';

import { API_BASE_URL, joinApiUrl } from '@/services/api-base-url';
import { authService, UserSession, UnauthorizedError } from '@/services/auth-service';
import { getStoredBookmark, setStoredBookmark } from '@/services/d1-bookmark';
import { FetchTimeoutError, fetchWithTimeout, type FetchWithTimeoutOptions } from '@/services/http-service';
import {
	DEFAULT_MAX_RETRIES,
	computeBackoffMs,
	isIdempotentMethod,
	isNetworkRetryable,
	sleep,
	type FailEvent,
	type ResolveEvent,
	type RetryAttempt,
	type RetryFailListener,
	type RetryListener,
	type RetryResolveListener,
} from '@/services/retry-policy';
import { UserRole, type User } from '@/types/models';
import { ApiResponseError } from '@/utils/api-error';
import { logger } from '@/utils/logger';

/**
 * Header the Worker's auth gate sets on its refusals (#692): a reason on a definitive 401
 * (`device_token_not_found`, `oauth_rejected`, …) and `unavailable` on the 503 it answers
 * when it could not verify the credentials at all (D1 overloaded, provider down).
 */
const AUTH_ERROR_HEADER = 'X-Auth-Error';

/**
 * A 401 WITHOUT a reason did not come from this API's gate as it is today — an older Worker
 * or something in between — so it proves nothing about the session. Re-drive it on this
 * schedule before believing it.
 */
const HEADERLESS_401_RECHECK_DELAYS_MS = [1_000, 2_000, 4_000];

/** Refusals a fresh sign-in cannot fix: there is no point refreshing a token for them. */
const REAUTH_CANNOT_HELP = new Set(['user_not_found', 'demo_org']);

/** `authError` value for a 401 that never carried a reason, even after the re-checks. */
export const UNCONFIRMED_AUTH_ERROR = 'unconfirmed';

// Re-exported for existing importers; auth-service must use the leaf module (import cycle).
export { API_BASE_URL, joinApiUrl };

const UPLOAD_TIMEOUT_MS = 180000;
const MAX_UPLOAD_ATTEMPTS = 3;

const getErrorName = (error: unknown): string => {
	if (!error || typeof error !== 'object') {
		return '';
	}
	const maybeName = (error as { name?: unknown }).name;
	return typeof maybeName === 'string' ? maybeName : '';
};

const getErrorMessage = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message.toLowerCase();
	}
	if (typeof error === 'string') {
		return error.toLowerCase();
	}
	return '';
};

const safeReadText = async (response: Response): Promise<string> => {
	try {
		return await response.text();
	} catch {
		return '';
	}
};

const isRetryableUploadError = (error: unknown): boolean => {
	if (error instanceof FetchTimeoutError) {
		return true;
	}

	const name = getErrorName(error).toLowerCase();
	if (name === 'aborterror' || name === 'timeouterror') {
		return true;
	}

	const message = getErrorMessage(error);
	return (
		message.includes('aborted')
		|| message.includes('timeout')
		|| message.includes('timed out')
		|| message.includes('network request failed')
		|| message.includes('failed to fetch')
		|| message.includes('load failed')
	);
};

// Allow callers to override the default retry policy per-request. The default
// retries 5xx/network errors on idempotent methods only — POST callers that
// know their endpoint is safe to retry can opt in with `retry: true`.
export type RetryOverride = boolean;

class ApiService {
	private azureMapsKey: string | null = null;
	private userListeners: ((user: User | null) => void)[] = [];
	private isReady = false;
	private mode: 'local' | 'remote' = 'remote';
	private retryListeners: RetryListener[] = [];
	private retryResolveListeners: RetryResolveListener[] = [];
	private retryFailListeners: RetryFailListener[] = [];
	/**
	 * The silent re-auth in progress, if any. A page load fires ~20 requests at once; when the
	 * session is dead they all 401 together, and each must wait on the SAME re-auth rather than
	 * refreshing the token and registering a device twenty times. Keyed by the refused session's
	 * LIFETIME (account + epoch): a 401 from another account — or from this account before it signed
	 * out and back in — must not ride on (and be replayed as) another lifetime's renewal.
	 */
	private reauthInFlight: { key: string; promise: Promise<UserSession | null> } | null = null;

	/**
	 * Refuse to re-send a request whose session has ended. EVERY path in `fetchApi` that waits and
	 * then re-sends — 5xx backoff, network backoff, the header-less 401 re-check — must call this
	 * after its wait: the user can sign out or switch accounts during the delay, and the old device
	 * token stays valid server-side (sign-out only forgets it locally), so a delayed POST would
	 * otherwise land after the session boundary, or go out under the next account's credentials
	 * (Codex review round 8 on #693). Throws like the stale-401 drop: no auth prompt is recorded.
	 */
	private assertSenderLive(session: UserSession, path: string): void {
		if (authService.isSessionLive(session)) return;
		logger.log(`Not re-sending ${path}: the session it was sent under has ended.`);
		throw new UnauthorizedError('Session ended before the request could be re-sent');
	}

	private reauthenticateOnce(refused: UserSession): Promise<UserSession | null> {
		const key = `${refused.id}:${authService.sessionEpochOf(refused)}`;
		if (this.reauthInFlight?.key === key) return this.reauthInFlight.promise;
		const promise = (async () => {
			try {
				return await authService.reauthenticate(refused);
			} catch (error) {
				console.warn('Silent re-authentication failed:', error);
				return null;
			} finally {
				// Only clear our own entry; another lifetime's renewal may have replaced it.
				if (this.reauthInFlight?.key === key) this.reauthInFlight = null;
			}
		})();
		this.reauthInFlight = { key, promise };
		return promise;
	}

	onRetry(callback: RetryListener): () => void {
		this.retryListeners.push(callback);
		return () => {
			this.retryListeners = this.retryListeners.filter(cb => cb !== callback);
		};
	}

	onRetryResolved(callback: RetryResolveListener): () => void {
		this.retryResolveListeners.push(callback);
		return () => {
			this.retryResolveListeners = this.retryResolveListeners.filter(cb => cb !== callback);
		};
	}

	onRetryFailed(callback: RetryFailListener): () => void {
		this.retryFailListeners.push(callback);
		return () => {
			this.retryFailListeners = this.retryFailListeners.filter(cb => cb !== callback);
		};
	}

	private emitRetry(event: RetryAttempt) {
		for (const listener of this.retryListeners) {
			try { listener(event); } catch (e) { console.warn('retry listener threw', e); }
		}
	}

	private emitRetryResolved(event: ResolveEvent) {
		for (const listener of this.retryResolveListeners) {
			try { listener(event); } catch (e) { console.warn('retry-resolved listener threw', e); }
		}
	}

	private emitRetryFailed(event: FailEvent) {
		for (const listener of this.retryFailListeners) {
			try { listener(event); } catch (e) { console.warn('retry-failed listener threw', e); }
		}
	}

	constructor() {
		// Listen to auth changes to load user data
		authService.onSessionChange(this.handleSessionChange.bind(this));
		this.initialize();
	}

	private async initialize() {
		try {
			// Wait for auth service to be initialized
			logger.log('ApiService: Waiting for auth service initialization...');
			await authService.waitForInitialization();
			const session = await authService.getSession();

			// Process the current session if one exists
			if (session) {
				logger.log('ApiService: Processing existing session during initialization');
				await this.handleSessionChange(session);
			}

			this.isReady = true;
			logger.log('ApiService: Initialized, session available:', !!session);
		} catch (error) {
			console.error('ApiService: Initialization failed:', error);
			this.isReady = true; // Mark as ready even if failed to prevent hanging
		}
	}

	async waitForReady(): Promise<void> {
		const maxWaitTime = 10000; // 10 seconds max wait
		const startTime = Date.now();

		while (!this.isReady) {
			if (Date.now() - startTime > maxWaitTime) {
				console.warn('ApiService: waitForReady timeout after 10 seconds');
				break;
			}
			await new Promise(resolve => setTimeout(resolve, 50));
		}
	}

	// Event listeners for React components
	onUserChange(callback: (user: User | null) => void): () => void {
		this.userListeners.push(callback);
		return () => {
			this.userListeners = this.userListeners.filter(cb => cb !== callback);
		};
	}

	private notifyUserListeners(user: User | null) {
		for (const listener of this.userListeners) {
			listener(user);
		}
	}

	/**
	 * Update the signed-in user AND tell the UI about it. Use this, never `authService.setUser`.
	 *
	 * ── WHY THIS EXISTS ──
	 *
	 * There is ONE store — `authService.currentUser`, which `apiService.getUser()` delegates
	 * to — but TWO parallel notification channels over it, and they reach different halves of
	 * the UI:
	 *
	 *   authService.onUserChange → useAuth().user   → tracker, print pages, calendar
	 *   apiService.onUserChange  → SessionProvider  → useApi().user → most permission gates
	 *
	 * `authService.setUser` fires only the first. So it correctly updates the value and secure
	 * storage, repaints the `useAuth` consumers, and leaves everything reading `useApi().user`
	 * on the stale object — with no error, because nothing is broken from its point of view.
	 *
	 * This method fires BOTH, making it a strict superset and the only safe way to write.
	 *
	 * Every internal call site already did the two steps by hand, correctly, six times over.
	 * That made the pairing a convention enforced by nothing, and the seventh caller (the
	 * dashboard-version toggle, #279) wrote only the first half: the PUT returned 200, the
	 * column changed, and the switch sat there — indistinguishable from a dead control, with
	 * no error anywhere to suggest otherwise.
	 *
	 * `notifyUserListeners` stays private so this remains the only way to do it.
	 */
	async setUser(user: User | null): Promise<void> {
		// The ONE place `authService.setUser` is called directly, by design.
		await authService.setUser(user);
		this.notifyUserListeners(user);
	}

	private async handleSessionChange(session: UserSession | null) {
		logger.log('ApiService: Session changed', session?.email, 'hasToken:', !!session?.accessToken);
		logger.log('Session:', session);

		if (session?.isDemoMode) {
			logger.log('ApiService: Demo mode enabled, disabling backend access and creating local user');
			this.mode = 'local';
			this.azureMapsKey = null;

			const now = new Date().toISOString();
			const demoUser: User = {
				id: session.id,
				email: session.email,
				name: session.name,
				role: UserRole.Admin,
				isAdmin: true,
				organizationId: 'demo',
				createdAt: now,
				updatedAt: now,
			};

			await this.setUser(demoUser);
			return;
		}

		this.mode = 'remote';

		if (session?.accessToken || session?.idToken || session?.deviceToken) {
			logger.log('ApiService: Loading user data for session');
			await this.loadUserData(session);
		} else {
			logger.log('ApiService: No token available, clearing user data');
			this.azureMapsKey = null;
			await this.setUser(null);
		}
	}

	async fetchApi(
		path: string,
		options?: FetchWithTimeoutOptions & { retry?: RetryOverride; __authRecheck?: number; __reauthed?: boolean },
		customSession?: UserSession | null,
		skipReadyCheck: boolean = false,
		rawResponse: boolean = false,
	): Promise<any> {
		if (this.mode === 'local') {
			throw new UnauthorizedError('Local mode - backend access disabled');
		}

		// Ensure the service is ready before making API calls (skip during initialization)
		if (!customSession && !skipReadyCheck) {
			await this.waitForReady();
		}

		const session = customSession || await authService.getSession();
		if (session?.isDemoMode) {
			throw new UnauthorizedError('Demo mode - backend access disabled');
		}

		// Prefer device token for persistent authentication, fall back to OAuth tokens
		let authHeader: string;
		if (session?.deviceToken) {
			authHeader = `Device ${session.deviceToken}`;
			logger.log('Using device token for authentication');
		} else {
			// Use ID token if available, fall back to access token
			const authToken = session?.accessToken || session?.idToken;
			if (!authToken) {
				console.error('No valid token available for API request:', path, {
					hasSession: !!session,
					sessionId: session?.id,
					hasAccessToken: !!session?.accessToken,
					hasIdToken: !!session?.idToken,
					hasDeviceToken: !!session?.deviceToken,
					provider: session?.provider,
				});
				throw new Error('No valid token found. Please sign in again.');
			}
			// Default to 'microsoft' since that's the only OAuth provider the app supports
			// (Google OAuth is not implemented - 'google' was a legacy default that caused issues)
			const provider = session?.provider || 'microsoft';
			authHeader = `Bearer ${authToken} ${provider}`;
			logger.log('Using OAuth token for authentication', { provider });
		}

		logger.log(`API request to: ${path}`, {
			authHeader,
			hasDeviceToken: !!session?.deviceToken,
			sessionProvider: session?.provider,
		});
		let requestOptions: FetchWithTimeoutOptions = { ...(options || {}) };
		const headers = new Headers(requestOptions.headers || {});
		headers.set('Authorization', authHeader);

		// Attach client version header
		try {
			const clientVersion = (Constants as any)?.expoConfig?.version || 'unknown';
			headers.set('x-client-version', String(clientVersion));
		} catch {
			console.error('Error attaching client version header');
		}

		// Replay the most recent D1 bookmark so the server can pin all
		// reads in this request to "≥ that bookmark" — read-your-writes
		// across requests in this browser tab. Server falls back to
		// 'first-primary' when no bookmark is present.
		const storedBookmark = getStoredBookmark();
		if (storedBookmark) {
			headers.set('X-D1-Bookmark', storedBookmark);
		}

		// Default to JSON for POST/PUT and enrich body with history context
		const method = (requestOptions.method || 'GET').toUpperCase();
		const isWrite = method === 'POST' || method === 'PUT' || method === 'DELETE';
		if (isWrite) {
			const body = requestOptions.body as any;
			const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
			// For multipart/form-data requests, do NOT set Content-Type manually or serialize body
			if (isFormData) {
				// If something previously set JSON content type, remove it so the runtime can set boundary
				if ((headers.get('Content-Type') || '').includes('application/json')) {
					headers.delete('Content-Type');
				}
			} else {
				const contentType = headers.get('Content-Type');
				if (!contentType) headers.set('Content-Type', 'application/json');
				if ((headers.get('Content-Type') || '').includes('application/json')) {
					let payload: any = {};
					if (requestOptions.body) {
						try { payload = typeof requestOptions.body === 'string' ? JSON.parse(requestOptions.body as any) : requestOptions.body; } catch {
							// Ignore JSON parsing errors, payload will remain empty object
						}
					}
					// attach history location from current user if available
					try {
						const user = await authService.getUser();
						if (user) {
							// Only send a REAL fix. The user object is built with
							// `lastLat: apiData.lastLat || null` below, so the key is always
							// present — an `!== undefined` test therefore attached
							// `historyLat: null` to every write in the app, which the server
							// stores as no location anyway.
							if (payload.historyLat === undefined && Number.isFinite(user.lastLat)) payload.historyLat = user.lastLat;
							if (payload.historyLng === undefined && Number.isFinite(user.lastLng)) payload.historyLng = user.lastLng;
						}
					} catch {
						// Ignore user fetch errors, payload will use default values
					}
					requestOptions = { ...requestOptions, body: JSON.stringify(payload) };
				}
			}
		}

		if (path.startsWith('/')) {
			path = path.slice(1);
		}

		// Resolve retry config. Default: retry idempotent methods on 5xx/network.
		// Callers can opt out (`retry: false`) or opt-in for POST when the endpoint
		// is safe (e.g. bulk-import chunks tagged with import_batch_id).
		const retryOverride = options?.retry;
		const retryEnabled = typeof retryOverride === 'boolean' ? retryOverride : isIdempotentMethod(method);
		const maxAttempts = DEFAULT_MAX_RETRIES + 1;
		const totalAttempts = retryEnabled ? maxAttempts : 1;

		let attempt = 0;
		let lastNetworkError: unknown = null;

		while (attempt < maxAttempts) {
			attempt += 1;
			try {
				const response = await fetchWithTimeout(joinApiUrl(API_BASE_URL, path), {
					...requestOptions,
					headers,
				});

				// Capture the post-handler D1 bookmark before any branching so
				// even error responses advance the bookmark when the server
				// did write work before failing. Header is opaque; the helper
				// stashes in sessionStorage (or in-memory on native).
				const responseBookmark = response.headers.get('X-D1-Bookmark');
				if (responseBookmark) {
					setStoredBookmark(responseBookmark);
				}

				// Allow callers (e.g., file downloads) to receive the raw Response
				if (rawResponse) {
					if (attempt > 1) {
						this.emitRetryResolved({ path, succeededOnAttempt: attempt });
					}
					return response;
				}

				logger.log(`API response for ${path}:`, {
					status: response.status,
					ok: response.ok,
					headers: Object.fromEntries(response.headers.entries()),
				});

				// 5xx: retry if eligible. We must consume the body now (response.text())
				// so the connection is freed before sleeping; the parsed text feeds
				// either the next-attempt log or the final thrown error.
				//
				// The auth gate's 503 (`X-Auth-Error: unavailable`) is retried for EVERY method: it
				// is answered before any handler runs, so even a POST did no work.
				const authUnavailable = response.status === 503 && response.headers.get(AUTH_ERROR_HEADER) === 'unavailable';
				const attemptBudget = authUnavailable ? maxAttempts : totalAttempts;
				if (response.status >= 500 && (retryEnabled || authUnavailable) && attempt < attemptBudget) {
					const text = await safeReadText(response);
					const retryAfter = response.headers.get('retry-after');
					const delayMs = computeBackoffMs(attempt, { retryAfter });
					console.warn(`API ${response.status} for ${path} (attempt ${attempt}/${attemptBudget}). Retrying in ${delayMs}ms.`, text.slice(0, 200));
					this.emitRetry({
						attempt,
						maxAttempts: attemptBudget,
						reason: 'server',
						status: response.status,
						delayMs,
						path,
					});
					await sleep(delayMs);
					if (session) this.assertSenderLive(session, path);
					continue;
				}

				if (!response.ok) {
					const text = await response.text();
					console.warn(`API error ${response.status} for ${path}: ${text}`);

					if (response.status === 401) {
						// A 401 means the handler did no work, so re-driving it is safe for every method.
						const reason = response.headers.get(AUTH_ERROR_HEADER);
						logger.log(`Authentication error (401, reason: ${reason ?? 'none'}).`);

						// No reason: not a definitive refusal from our gate (an older Worker, or something
						// in between). Seen as one 401 among hundreds of 200 polls on the QB import
						// monitor. Re-drive with backoff before believing it.
						const recheck = options?.__authRecheck ?? 0;
						if (!reason && recheck < HEADERLESS_401_RECHECK_DELAYS_MS.length) {
							console.warn(`API 401 for ${path} without ${AUTH_ERROR_HEADER} — re-checking (${recheck + 1}/${HEADERLESS_401_RECHECK_DELAYS_MS.length}).`);
							await sleep(HEADERLESS_401_RECHECK_DELAYS_MS[recheck]);
							// Pin the re-check to the sender's lifetime: resolve the session to re-send with NOW,
							// check both it and the sender are live, and pass it explicitly so the re-send cannot
							// re-read a session that changed hands in between.
							const resendAs = customSession ?? await authService.getSession();
							// Both live ⇒ the same lifetime (only one is live at a time). A live sender implies a
							// current session, so `resendAs` is non-null whenever the first check passes.
							if (session) this.assertSenderLive(session, path);
							if (resendAs) this.assertSenderLive(resendAs, path);
							return this.fetchApi(
								path,
								{ ...requestOptions, retry: retryOverride, __authRecheck: recheck + 1, __reauthed: options?.__reauthed },
								resendAs,
								skipReadyCheck,
								rawResponse,
							);
						}

						// The session really is refused. Try ONE silent re-auth (refresh the OAuth token,
						// register a fresh device token) — shared by every request refused at the same
						// moment — and replay. Only if that fails does the user get asked to sign in.
						const authError = reason ?? UNCONFIRMED_AUTH_ERROR;
						if (session && !options?.__reauthed && !REAUTH_CANNOT_HELP.has(authError)) {
							const recovered = await this.reauthenticateOnce(session);
							// Never replay the refused request — possibly a POST — as anyone but its sender.
							if (recovered?.id === session.id) {
								logger.log('Silent re-authentication succeeded, replaying request');
								return this.fetchApi(
									path,
									{ ...requestOptions, retry: retryOverride, __reauthed: true },
									recovered,
									skipReadyCheck,
									rawResponse,
								);
							}
						}

						// Refused for a session lifetime that has since ended (signed out — maybe replaced, maybe by
						// the same account signing back in): that says nothing about the session live now.
						if (session && !authService.isSessionLive(session)) {
							logger.log(`Dropping a 401 (${authError}) for a session that is no longer signed in.`);
							throw new UnauthorizedError();
						}

						logger.log(`Session refused (${authError}) and could not be renewed. Asking the user to sign in.`);
						authService.setAuthError(authError);
						throw new UnauthorizedError();
					}

					if (response.status === 403) {
						logger.log('Forbidden error', text);
						throw new Error(`API error ${response.status}: ${text}`);
					}

					if (text.startsWith('{')) {
						try {
							const error = JSON.parse(text);
							if (error.message) {
								// Same message as ever; the code and body ride along for callers that branch.
								throw new ApiResponseError(
									`API error ${response.status}: ${error.message}`,
									response.status,
									typeof error.code === 'string' ? error.code : undefined,
									error,
								);
							}
						} catch (parseErr) {
							if (parseErr instanceof Error && parseErr.message.startsWith('API error')) {
								throw parseErr;
							}
							// fall through to the generic throw below
						}
					}

					throw new Error(`API error ${response.status}: ${text}`);
				}

				// 2xx — success. If we retried, notify listeners so the toast can dismiss.
				if (attempt > 1) {
					this.emitRetryResolved({ path, succeededOnAttempt: attempt });
				}

				const contentType = response.headers.get('content-type');
				if (contentType?.includes('application/json')) {
					return await response.json();
				} else {
					return await response.text();
				}
			} catch (error) {
				const msg = error instanceof Error ? error.message : String(error);

				// The CALLER cancelled (e.g. a superseded preview): not a failure, and never retried —
				// isNetworkRetryable counts every AbortError, which would re-send what was abandoned.
				if (options?.signal?.aborted) throw error;

				// 4xx (including auth) does not retry — log quietly for permission
				// errors (matches the original behavior) and re-throw.
				const is4xx = /^API error 4\d\d:/.test(msg);
				if (error instanceof UnauthorizedError || is4xx) {
					if (error instanceof UnauthorizedError || msg.includes('API error 401') || msg.includes('API error 403')) {
						console.warn(`API request rejected for ${path}:`, msg);
					} else {
						console.warn(`API client error for ${path}:`, msg);
					}
					throw error;
				}

				// Network/timeout errors: retry if eligible. Network failures are safe
				// for ALL methods because the server may not have received the request,
				// but we still gate on retryEnabled so callers can opt out.
				const isNetworkErr = isNetworkRetryable(error);
				if (isNetworkErr && retryEnabled && attempt < totalAttempts) {
					lastNetworkError = error;
					const delayMs = computeBackoffMs(attempt);
					console.warn(`Network error for ${path} (attempt ${attempt}/${totalAttempts}): ${msg}. Retrying in ${delayMs}ms.`);
					this.emitRetry({
						attempt,
						maxAttempts: totalAttempts,
						reason: error instanceof FetchTimeoutError ? 'timeout' : 'network',
						delayMs,
						path,
					});
					await sleep(delayMs);
					if (session) this.assertSenderLive(session, path);
					continue;
				}

				// Final 5xx (rethrown above) or exhausted network retry — notify
				// listeners so any "retrying..." toast can flip to error state.
				if (/^API error 5\d\d:/.test(msg) || isNetworkErr) {
					this.emitRetryFailed({ path, attempts: attempt });
				}

				console.error(`API request failed for ${path}:`, error);
				throw error;
			}
		}

		// Loop exited without returning — means we exhausted retries on 5xx without
		// hitting the throw branch (e.g. final attempt also 5xx but the loop ended).
		// This is a safety net; in practice the last iteration falls through to the
		// `!response.ok` branch above.
		this.emitRetryFailed({ path, attempts: attempt });
		throw lastNetworkError instanceof Error
			? lastNetworkError
			: new Error(`API request to ${path} failed after ${attempt} attempts`);
	}

	// Helper for downloading binary files with auth headers applied
	async downloadFile(
		path: string,
		options?: FetchWithTimeoutOptions,
		customSession?: UserSession | null,
		skipReadyCheck: boolean = false,
	): Promise<Response> {
		// Check if we're in demo mode and this is a file request
		if (this.mode === 'local' && path.startsWith('/files/')) {
			const storageKey = path.replace('/files/', '');
			// Dynamic import to avoid circular dependency
			const { demoDataService } = await import('./demo-data-service');
			return demoDataService.downloadFile(storageKey);
		}

		return this.fetchApi(path, options, customSession, skipReadyCheck, true);
	}

	// Helper for uploading files with auth headers applied
	async uploadFile(formData: FormData): Promise<any> {
		// Check if we're in demo mode
		if (this.mode === 'local') {
			// Dynamic import to avoid circular dependency
			const { demoDataService } = await import('./demo-data-service');
			return demoDataService.uploadFile(formData);
		}

		let lastError: unknown;

		for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
			try {
				return await this.fetchApi('/files', {
					method: 'POST',
					body: formData,
					timeoutMs: UPLOAD_TIMEOUT_MS,
				});
			} catch (error) {
				lastError = error;
				const shouldRetry = attempt < MAX_UPLOAD_ATTEMPTS && isRetryableUploadError(error);
				if (!shouldRetry) {
					throw error;
				}

				console.warn(`Upload attempt ${attempt} failed with retryable error. Retrying...`, {
					errorName: getErrorName(error),
					errorMessage: error instanceof Error ? error.message : String(error),
				});
			}
		}

		throw lastError instanceof Error ? lastError : new Error('Upload failed');
	}

	private async loadUserData(session?: UserSession, attempt: number = 0): Promise<void> {
		// For Apple users, verify with backend first
		if (session?.provider === 'apple') {
			logger.log('Apple user detected, verifying with backend');
			try {
				// Try to get user data from backend using Apple JWT
				const apiData = await this.fetchApi('/me', undefined, session, true); // Skip ready check during initialization

				logger.log('API response user data for Apple user:', apiData);

				// Create user object from API response
				const user: User = {
					id: apiData.id,
					email: apiData.email,
					name: apiData.name,
					role: apiData.role || UserRole.Support,
					isAdmin: apiData.isAdmin || false,
					organizationId: apiData.organizationId || 'primary',
					hourlyRate: apiData.hourlyRate || 0,
					lastLat: apiData.lastLat || null,
					lastLng: apiData.lastLng || null,
					// See the note on the non-Apple branch below: this allowlist STRIPS any
					// field it does not name, so a per-user preference has to be added here too.
					createdAt: apiData.createdAt || new Date().toISOString(),
					updatedAt: apiData.updatedAt || new Date().toISOString(),
				};

				logger.log('Created Apple user from API data', user);
				await this.setUser(user);

				if (apiData.azure_maps_key) {
					this.azureMapsKey = apiData.azure_maps_key;
				}
				return;
			} catch (error) {
				console.error('Error loading Apple user data from backend:', error);
				// If backend verification fails, don't allow login
				await this.setUser(null);
				return;
			}
		}

		// For non-Apple users, try backend API
		try {
			const apiData = await this.fetchApi('/me', undefined, session, true); // Skip ready check during initialization

			logger.log('API response user data:', apiData);

			/**
			 * The session user, rebuilt from `/me`.
			 *
			 * AN EXPLICIT ALLOWLIST, NOT A SPREAD. Anything not named here is silently
			 * dropped, even when the API sends it — which makes this the LAST of four
			 * places a new per-user field has to be added, and the easiest to miss:
			 *
			 *   1. the D1 column                          (migration)
			 *   2. the device-token mapping in api/index.ts
			 *   3. `formatUser` in api/users.ts           (users list + PUT)
			 *   4. HERE                                    (what `useApi().user` becomes)
			 *
			 * Miss any one and the symptom is identical and silent: the row is correct, the
			 * PUT returns 200, and every client gate keeps reading the default forever.
			 */
			const user: User = {
				id: apiData.id,
				email: apiData.email,
				name: apiData.name,
				role: apiData.role || UserRole.Support,
				isAdmin: apiData.isAdmin || false,
				organizationId: apiData.organizationId || 'primary',
				hourlyRate: apiData.hourlyRate || 0,
				lastLat: apiData.lastLat || null,
				lastLng: apiData.lastLng || null,
				createdAt: apiData.createdAt || new Date().toISOString(),
				updatedAt: apiData.updatedAt || new Date().toISOString(),
			};

			logger.log('Created user from API data', user);
			await this.setUser(user);

			if (apiData.azure_maps_key) {
				this.azureMapsKey = apiData.azure_maps_key;
			}
		} catch (error) {
			console.error('Error loading user data:', error);
			await this.setUser(null);
		}
	}

	async searchAddress(query: string, limit: number = 5): Promise<Array<{
		id: string;
		label: string;
		lat: number;
		lng: number;
		raw?: any;
	}>> {
		logger.log('*** Search Address ***', {
			azureMapsKey: this.azureMapsKey,
			query,
			limit,
		});

		if (!this.azureMapsKey || !query.trim()) return [];

		const url = `https://atlas.microsoft.com/search/address/json?api-version=1.0&typeahead=true&limit=${limit}&query=${encodeURIComponent(query)}&subscription-key=${this.azureMapsKey}`;

		try {
			const res = await fetchWithTimeout(url);
			const data = await res.json();

			if (data.results) {
				return data.results.map((result: any, index: number) => ({
					id: `${result.id || index}`,
					label: result.address.freeformAddress,
					lat: result.position.lat,
					lng: result.position.lon,
					raw: result,
				}));
			}

			return [];
		} catch (error) {
			console.error('Error searching address:', error);
			return [];
		}
	}

	async getCurrentUser(): Promise<User | null> {
		return authService.getUser();
	}

	getApiBaseUrl(): string {
		return API_BASE_URL;
	}

	/**
	 * Assume the identity of another user (admin only)
	 * @param targetUserId The ID of the user to impersonate
	 * @param reason Optional reason for impersonation
	 * @returns Device token for the impersonated user
	 */
	async assumeUser(targetUserId: string, reason?: string): Promise<string> {
		const response = await this.fetchApi('/assume', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				targetUserId,
				reason,
			}),
		});

		if (!response.deviceToken) {
			throw new Error('Invalid response from assume endpoint');
		}

		return response.deviceToken;
	}
}

// Export singleton instance
export const apiService = new ApiService();
